/**
 * PaywallModal — non-dismissible overlay when the user's trial expires.
 *
 * Props:
 *   open       — boolean, controlled by PlanGate
 *   planStatus — object from GET /api/billing/status
 */
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Check, MessageCircle, Zap, Users, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const WHATSAPP_NUMBER = '918095526797';

const PAID_PLANS = [
  {
    id: 'pro',
    name: 'Pro',
    price: '₹2,500',
    icon: Zap,
    color: '#00E599',
    waMsg: "Hi Ashish, my AutoQA trial expired. I'd like to upgrade to Pro (₹2,500/month). Please share payment details.",
  },
  {
    id: 'team',
    name: 'Team',
    price: '₹6,500',
    icon: Users,
    color: '#D946EF',
    waMsg: "Hi Ashish, my AutoQA trial expired. I'd like to upgrade to Team (₹6,500/month). Please share payment details.",
  },
  {
    id: 'business',
    name: 'Business',
    price: '₹16,000',
    icon: Building2,
    color: '#F59E0B',
    waMsg: "Hi Ashish, my AutoQA trial expired. I'd like to upgrade to Business (₹16,000/month). Please share payment details.",
  },
];

const PRO_FEATURES = [
  'Unlimited AI test generation',
  'Unlimited test executions',
  'API testing with collection import',
  'Visual regression comparisons',
  'Team collaboration & sharing',
  'Self-healing test maintenance',
];

export default function PaywallModal({ open, planStatus }) {
  if (!open) return null;

  const isExpired = planStatus?.status === 'expired' || planStatus?.status === 'cancelled';

  const handleUpgrade = (plan) => {
    const msg = encodeURIComponent(plan.waMsg);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`, '_blank');
  };

  return (
    <AnimatePresence>
      {open && (
        /* Backdrop — clicking does nothing (non-dismissible) */
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/90 backdrop-blur-sm overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="w-full max-w-lg bg-[#0D1117] border border-white/10 rounded-2xl p-8 shadow-2xl my-8"
            onClick={e => e.stopPropagation()}
          >
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center">
                <Lock className="w-8 h-8 text-red-400" />
              </div>
            </div>

            {/* Heading */}
            <h2
              className="text-2xl font-bold text-center text-white mb-2"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
            >
              {isExpired ? 'Trial Expired' : 'Upgrade Required'}
            </h2>
            <p className="text-slate-400 text-center text-sm mb-6">
              {isExpired
                ? 'Your 7-day free trial has ended. Choose a plan to keep using AutoQA.ai.'
                : 'This feature requires an active paid plan.'}
            </p>

            {/* Features */}
            <div className="space-y-2 mb-6">
              {PRO_FEATURES.map(feat => (
                <div key={feat} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#00E599]/10 border border-[#00E599]/30 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-[#00E599]" />
                  </div>
                  <span className="text-sm text-slate-300">{feat}</span>
                </div>
              ))}
            </div>

            {/* Plan buttons */}
            <div className="space-y-3 mb-4">
              {PAID_PLANS.map(plan => {
                const Icon = plan.icon;
                return (
                  <Button
                    key={plan.id}
                    className="w-full h-11 font-semibold flex items-center justify-between px-4 bg-white/5 border border-white/10 hover:bg-white/10 text-white"
                    onClick={() => handleUpgrade(plan)}
                  >
                    <span className="flex items-center gap-2">
                      <Icon className="w-4 h-4" style={{ color: plan.color }} />
                      {plan.name} Plan
                    </span>
                    <span className="flex items-center gap-2 text-sm">
                      <span className="text-slate-300">{plan.price}<span className="text-slate-500 text-xs">/mo</span></span>
                      <MessageCircle className="w-4 h-4 text-green-400" />
                    </span>
                  </Button>
                );
              })}
            </div>

            <p className="text-center text-xs text-slate-600">
              Contact via WhatsApp · Ashish activates your plan within minutes
            </p>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
